// src/pages/Integrations/AllVue/ConnectionMethodCards.tsx
import { css } from '@emotion/react';
import { Box, Radio, Stack, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { AllVueFormValues, ConnectionType } from './allVueConfig.schema';

const optionCardCss = css`
  border: 1px solid var(--mui-colors-neutral-30, #b6bbc4);
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const selectedCss = css`
  border-color: var(--mui-palette-secondary-main, #6a45d3);
  background: var(--mui-palette-secondary-50, #f8f8ff);
`;

interface MethodOption {
  value: ConnectionType;
  label: string;
  description: string;
}

const OPTIONS: MethodOption[] = [
  {
    value: 'snowflake',
    label: 'Snowflake Database',
    description: 'Connect to AllVue data via Snowflake database share',
  },
  {
    value: 'oauth',
    label: 'OAuth (Client Credentials)',
    description: 'Connect to AllVue using OAuth 2.0 client credentials flow',
  },
];

export function ConnectionMethodCards() {
  const { control } = useFormContext<AllVueFormValues>();

  return (
    <Controller
      name='connectionType'
      control={control}
      render={({ field }) => (
        <Stack direction='row' gap='0.75rem' flexWrap='wrap'>
          {OPTIONS.map((opt) => {
            const selected = field.value === opt.value;
            return (
              <Box
                key={opt.value}
                role='radio'
                aria-checked={selected}
                tabIndex={0}
                onClick={() => field.onChange(opt.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    field.onChange(opt.value);
                  }
                }}
                css={[optionCardCss, selected && selectedCss]}
                sx={{ flex: '1 1 0', minWidth: '240px' }}
              >
                <Radio
                  checked={selected}
                  value={opt.value}
                  tabIndex={-1}
                  inputProps={{ 'aria-hidden': true, tabIndex: -1 }}
                  sx={{ p: 0, mt: '2px' }}
                />
                <Box>
                  <Typography variant='body1' fontWeight='medium'>
                    {opt.label}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {opt.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    />
  );
}
